import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../services/api';

const ProfileSettings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email] = useState(user?.email || '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatarUrl || null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch fresh profile data on mount to get a stable signed URL for preview
  // Do NOT call updateUser here to avoid re-render loops and repeated permission fetches
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await userAPI.getProfile();
        if (profile?.avatarUrl) {
          setAvatarPreview(profile.avatarUrl);
        }
        if (profile?.firstName) setFirstName(profile.firstName);
        if (profile?.lastName) setLastName(profile.lastName);
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };
    fetchProfile();
  }, []);

  // Update avatar preview when user avatarUrl changes
  useEffect(() => {
    if (user?.avatarUrl && !avatarFile) {
      setAvatarPreview(user.avatarUrl);
    }
  }, [user?.avatarUrl, avatarFile]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    try {
      setLoadingSave(true);
      const res = await userAPI.updateProfile(firstName, lastName);
      if (res?.user) {
        updateUser(res.user);
      }
      setMessage('Profile updated successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoadingSave(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    
    if (!file) {
      setAvatarFile(null);
      setAvatarPreview(user?.avatarUrl || null);
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
      setAvatarFile(null);
      setAvatarPreview(user?.avatarUrl || null);
      // Reset file input
      e.target.value = '';
      return;
    }

    setError(null);
    setAvatarFile(file);
    
    // Create preview from selected file
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    setMessage(null);
    setError(null);
    try {
      setLoadingAvatar(true);
      const res = await userAPI.uploadAvatar(avatarFile);
      if (res?.user) {
        updateUser(res.user);
      } else if (res?.avatarUrl) {
        updateUser({ avatarUrl: res.avatarUrl });
      }
      setAvatarFile(null);
      setAvatarPreview(null);
      setMessage('Profile picture updated');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setLoadingAvatar(false);
    }
  };

  const handleRequestPasswordReset = async () => {
    setMessage(null);
    setError(null);
    try {
      const res = await userAPI.requestPasswordReset();
      setMessage(res?.message || 'Password reset email sent');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send password reset email');
    }
  };

  return (
    <Layout title="Profile Settings" subtitle="Manage your account information">
      <div className="max-w-3xl mx-auto">
        {message && (
          <div className="mb-4 p-3 rounded bg-green-50 text-green-800 border border-green-200 text-sm">{message}</div>
        )}
        {error && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-800 border border-red-200 text-sm">{error}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white rounded-lg border shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="mt-1 block w-full bg-gray-100 border border-gray-200 rounded-md px-3 py-2 text-gray-600"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 rounded-md bg-gray-900 text-white text-sm disabled:opacity-50"
                  disabled={loadingSave}
                >
                  {loadingSave ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleRequestPasswordReset}
                  className="inline-flex items-center px-4 py-2 rounded-md border text-sm"
                >
                  Send Password Reset Email
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Picture</h2>
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border mb-4">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xl">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </div>
                )}
              </div>
              
              {/* Hidden file input */}
              <input 
                type="file" 
                id="avatar-upload"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" 
                onChange={handleAvatarChange}
                className="hidden"
              />
              
              {/* Custom file input button */}
              <label 
                htmlFor="avatar-upload"
                className="w-full mb-2 cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Choose File
              </label>
              
              {/* Show selected file name */}
              {avatarFile && (
                <p className="text-xs text-gray-500 mb-2 text-center w-full truncate px-2">
                  {avatarFile.name}
                </p>
              )}
              
              <button
                type="button"
                onClick={handleAvatarUpload}
                className="w-full mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md bg-gray-900 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!avatarFile || loadingAvatar}
              >
                {loadingAvatar ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfileSettings;


